import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup, ListSubheader, Menu, MenuItem, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';

/**
 * Le calendrier des offres du moment (migration 246, `services/offresDuMoment.js` côté serveur) :
 * ce que la suggestion du bilan du samedi, et demain la lettre du dimanche, proposent au parent
 * selon la période. La vue par semaine montre d'un coup d'œil les semaines SANS offre (la
 * suggestion habituelle part alors) et celles dont l'offre ne partira pas, faute de produit en
 * vente (« Grimoire et pass »).
 *
 * La période d'une offre remplit le calendrier par défaut ; une case de la vue se règle à part,
 * pour UNE semaine et UN public (migration 247) : retirer l'offre cette semaine-là, ou en placer
 * une autre. Supprimer une offre l'efface de toutes ses semaines.
 *
 * Une offre a ses SURFACES (migration 261) : les mails, et le bandeau texte du site (en haut de
 * l'accueil, en tête de la boutique). Le site n'a ni public ni réglage de semaine : la vue par
 * semaine ne montre que les mails.
 */

const PUBLICS = [
  { cle: 'free', libelle: 'Gratuit' },
  { cle: 'premium', libelle: 'Aventurier' },
  { cle: 'premium_plus', libelle: 'Champion' },
];
const SURFACES = [
  { cle: 'mail', libelle: 'Mails au parent' },
  { cle: 'site_accueil', libelle: 'Site : accueil' },
  { cle: 'site_boutique', libelle: 'Site : boutique' },
];
const SEMAINES = 40;
const VIDE = {
  titre: '', texte: '', bouton: '', lien: '/dashboard/upgrade', produitCode: '', publics: ['free'],
  du: '', au: '', priorite: 0, actif: true, note: '', surfaces: ['mail'], lienSite: '',
};

const jourCourt = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const jourLong = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const aujourdhui = () => new Date().toISOString().slice(0, 10);

/** Une case de la vue : ce qui partira cette semaine pour ce public. Un clic ouvre le menu de la case. */
function CelluleSemaine({ cellule, onMenu }) {
  const ouvrirMenu = (e) => onMenu(e.currentTarget);
  if (cellule.retenue) {
    const placee = cellule.reglage === 'placee';
    return (
      <Tooltip title={placee ? 'Placée à la main pour cette semaine.' : 'Selon la période de l’offre.'}>
        <Chip size="small" color="success" variant={placee ? 'filled' : 'outlined'}
          label={placee ? `${cellule.retenue.titre} (cette semaine)` : cellule.retenue.titre} onClick={ouvrirMenu} />
      </Tooltip>
    );
  }
  if (cellule.reglage === 'sans') {
    return (
      <Tooltip title="Retirée à la main pour cette semaine : la suggestion habituelle part.">
        <Chip size="small" variant="outlined" label="Retirée cette semaine" onClick={ouvrirMenu} sx={{ borderStyle: 'dashed' }} />
      </Tooltip>
    );
  }
  if (cellule.bloquees.length) {
    const b = cellule.bloquees[0];
    const pourquoi = b.raison === 'inactive' ? 'désactivée' : 'produit fermé';
    return (
      <Tooltip title={b.raison === 'inactive' ? 'Cette offre est désactivée : elle ne partira pas.' : `Le produit ${b.produitCode} n’est pas en vente : cette offre ne partira pas.`}>
        <Chip size="small" color="warning" variant="outlined" label={`${b.titre} : ${pourquoi}`} onClick={ouvrirMenu} />
      </Tooltip>
    );
  }
  return <Chip size="small" variant="outlined" label="Sans offre" onClick={ouvrirMenu} />;
}

export default function OffresDuMomentScreen() {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [edition, setEdition] = useState(null); // { id?, ...champs }
  const [erreurEdition, setErreurEdition] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [menu, setMenu] = useState(null); // { ancre, semaine, publicCle }

  const charger = useCallback(async () => {
    try {
      const r = await AdminService.getOffresDuMoment({ semaines: SEMAINES });
      setDonnees(r);
      setErreur(null);
    } catch (e) {
      setErreur(e?.response?.data?.error || 'Lecture impossible');
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const produitsEnVente = useMemo(
    () => new Set((donnees?.produits || []).filter((p) => p.actif).map((p) => p.code)),
    [donnees],
  );
  const semainesSansOffre = useMemo(
    () => (donnees?.semaines || []).filter((s) => !s.publics.free.retenue).length,
    [donnees],
  );
  const lundiCourant = useMemo(() => {
    const d = new Date(`${aujourdhui()}T12:00:00Z`);
    const j = d.getUTCDay();
    d.setUTCDate(d.getUTCDate() + (j === 0 ? -6 : 1 - j));
    return d.toISOString().slice(0, 10);
  }, []);

  const ouvrir = (id) => {
    const o = (donnees?.offres || []).find((x) => x.id === id);
    if (o) { setErreurEdition(null); setEdition({
      ...o, produitCode: o.produitCode || '', note: o.note || '', lienSite: o.lienSite || '', surfaces: o.surfaces || ['mail'],
    }); }
  };
  const nouvelle = () => { setErreurEdition(null); setEdition({ ...VIDE }); };
  const champ = (cle) => (e) => setEdition((x) => ({ ...x, [cle]: e.target.value }));
  const basculerPublic = (cle) => setEdition((x) => ({
    ...x, publics: x.publics.includes(cle) ? x.publics.filter((p) => p !== cle) : [...x.publics, cle],
  }));

  const basculerSurface = (cle) => setEdition((x) => ({
    ...x, surfaces: x.surfaces.includes(cle) ? x.surfaces.filter((v) => v !== cle) : [...x.surfaces, cle],
  }));
  const surLeSite = (x) => x.surfaces.some((v) => v !== 'mail');

  const regler = async (action) => {
    const { semaine, publicCle } = menu;
    setMenu(null);
    try {
      if (action === 'calendrier') await AdminService.rendreSemaineAuCalendrier(semaine.lundi, publicCle);
      else await AdminService.reglerSemaineOffre(semaine.lundi, publicCle, action === 'sans' ? null : action);
      await charger();
    } catch (e) {
      setErreur(e?.response?.data?.error || 'Réglage impossible');
    }
  };

  const enregistrer = async () => {
    setErreurEdition(null);
    if (!edition.surfaces.length) { setErreurEdition('Choisissez au moins un endroit où l’offre paraît.'); return; }
    const corps = {
      ...edition, priorite: Number(edition.priorite) || 0, produitCode: edition.produitCode || null,
      lienSite: surLeSite(edition) ? edition.lienSite : '',
    };
    setEnCours(true);
    try {
      if (edition.id) await AdminService.modifierOffreDuMoment(edition.id, corps);
      else await AdminService.creerOffreDuMoment(corps);
      setEdition(null);
      await charger();
    } catch (e) {
      setErreurEdition(e?.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setEnCours(false);
    }
  };

  const supprimer = async () => {
    if (!edition?.id || !window.confirm(`Supprimer l’offre « ${edition.titre} » de toutes ses semaines ? Pour une seule semaine, utilisez plutôt la case de la semaine.`)) return;
    setEnCours(true);
    try {
      await AdminService.supprimerOffreDuMoment(edition.id);
      setEdition(null);
      await charger();
    } catch (e) {
      setErreurEdition(e?.response?.data?.error || 'Suppression impossible');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3, maxWidth: 1200 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 1 }}>
          <Typography variant="h5">Calendrier des offres</Typography>
          <Button variant="contained" onClick={nouvelle}>Nouvelle offre</Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
          L’offre qui couvre le week-end part dans le bilan du samedi (et, demain, dans la lettre du dimanche).
          Sans offre, la suggestion habituelle part : Aventurier pour un parent sans abonnement, Champion pour
          un Aventurier. Une offre liée à un produit ne part que si ce produit est en vente (« Grimoire et pass »).
        </Typography>
        {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
        {!donnees ? <CircularProgress /> : (
          <>
            <Alert severity={semainesSansOffre ? 'info' : 'success'} sx={{ mb: 2 }}>
              {semainesSansOffre
                ? `${semainesSansOffre} semaine(s) sur ${donnees.semaines.length} sans offre pour les parents sans abonnement.`
                : 'Chaque semaine a une offre pour les parents sans abonnement.'}
            </Alert>

            <Card sx={{ mb: 3 }}>
              <CardHeader title="Semaine par semaine"
                subheader="L’offre qui partira le week-end de chaque semaine. Cliquez sur une case pour retirer l’offre cette semaine-là, en placer une autre, ou revenir au calendrier." />
              <CardContent>
                <TableContainer sx={{ maxHeight: 560 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Semaine</TableCell>
                        {PUBLICS.map((p) => <TableCell key={p.cle}>{p.libelle}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {donnees.semaines.map((s) => (
                        <TableRow key={s.lundi} selected={s.lundi === lundiCourant}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2">du {jourCourt(s.lundi)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              envois les {jourCourt(s.samedi)} et {jourCourt(s.dimanche)}
                            </Typography>
                          </TableCell>
                          {PUBLICS.map((p) => (
                            <TableCell key={p.cle}>
                              <CelluleSemaine cellule={s.publics[p.cle]}
                                onMenu={(ancre) => setMenu({ ancre, semaine: s, publicCle: p.cle })} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Les offres" />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Période</TableCell>
                        <TableCell>Offre</TableCell>
                        <TableCell>Où</TableCell>
                        <TableCell>Publics</TableCell>
                        <TableCell>Produit</TableCell>
                        <TableCell align="center">Active</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {donnees.offres.map((o) => (
                        <TableRow key={o.id} sx={{ opacity: o.actif ? 1 : 0.5 }}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{jourCourt(o.du)} → {jourCourt(o.au)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{o.titre}</Typography>
                            {o.note && <Typography variant="caption" color="text.secondary">{o.note}</Typography>}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {SURFACES.filter((x) => (o.surfaces || ['mail']).includes(x.cle)).map((x) => (
                                <Chip key={x.cle} size="small" variant="outlined" label={x.libelle} />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {(o.surfaces || ['mail']).includes('mail') && PUBLICS.filter((p) => o.publics.includes(p.cle)).map((p) => <Chip key={p.cle} size="small" label={p.libelle} />)}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {!o.produitCode ? <Typography variant="caption" color="text.secondary">abonnement</Typography> : (
                              <Chip size="small" variant="outlined" color={produitsEnVente.has(o.produitCode) ? 'success' : 'warning'}
                                label={`${o.produitCode}${produitsEnVente.has(o.produitCode) ? '' : ' : fermé'}`} />
                            )}
                          </TableCell>
                          <TableCell align="center">{o.actif ? 'oui' : 'non'}</TableCell>
                          <TableCell align="right"><Button size="small" onClick={() => ouvrir(o.id)}>Modifier</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}

        <Menu anchorEl={menu?.ancre} open={!!menu} onClose={() => setMenu(null)}>
          {menu && (() => {
            const cellule = menu.semaine.publics[menu.publicCle];
            const libelle = PUBLICS.find((p) => p.cle === menu.publicCle)?.libelle;
            const actuelle = cellule.retenue?.id ?? cellule.bloquees[0]?.id ?? null;
            const possibles = (donnees?.offres || []).filter((o) => o.actif && (o.surfaces || ['mail']).includes('mail') && o.publics.includes(menu.publicCle));
            return [
              <ListSubheader key="t">Semaine du {jourCourt(menu.semaine.lundi)} · {libelle}</ListSubheader>,
              cellule.reglage && <MenuItem key="cal" onClick={() => regler('calendrier')}>Revenir au calendrier (la période des offres)</MenuItem>,
              cellule.reglage !== 'sans' && <MenuItem key="sans" onClick={() => regler('sans')}>Retirer l’offre cette semaine</MenuItem>,
              <Divider key="d1" />,
              <ListSubheader key="p">Placer cette semaine</ListSubheader>,
              ...possibles.map((o) => (
                <MenuItem key={`o${o.id}`} selected={cellule.retenue?.id === o.id} onClick={() => regler(o.id)}>
                  {o.titre}{o.produitCode && !produitsEnVente.has(o.produitCode) ? ' (produit fermé)' : ''}
                </MenuItem>
              )),
              actuelle && <Divider key="d2" />,
              actuelle && <MenuItem key="mod" onClick={() => { setMenu(null); ouvrir(actuelle); }}>Modifier l’offre (toutes ses semaines)</MenuItem>,
            ].filter(Boolean);
          })()}
        </Menu>

        <Dialog open={!!edition} onClose={() => !enCours && setEdition(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{edition?.id ? 'Modifier l’offre' : 'Nouvelle offre'}</DialogTitle>
          {edition && (
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {erreurEdition && <Alert severity="error">{erreurEdition}</Alert>}
                {edition.id && (
                  <Typography variant="caption" color="text.secondary">
                    Ces réglages valent pour toutes les semaines de l’offre. Pour la retirer d’une seule semaine,
                    cliquez sur la case de cette semaine dans la vue.
                  </Typography>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField id="offre-du" label="Du" type="date" value={edition.du} onChange={champ('du')} InputLabelProps={{ shrink: true }} fullWidth
                    helperText={edition.du ? jourLong(edition.du) : ' '} />
                  <TextField id="offre-au" label="Au (inclus)" type="date" value={edition.au} onChange={champ('au')} InputLabelProps={{ shrink: true }} fullWidth
                    helperText={edition.au ? jourLong(edition.au) : ' '} />
                </Stack>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Où l’offre paraît</Typography>
                  <FormGroup row>
                    {SURFACES.map((x) => (
                      <FormControlLabel key={x.cle} label={x.libelle}
                        control={<Checkbox id={`offre-surface-${x.cle}`} checked={edition.surfaces.includes(x.cle)} onChange={() => basculerSurface(x.cle)} />} />
                    ))}
                  </FormGroup>
                  {surLeSite(edition) && (
                    <Typography variant="caption" color="text.secondary">
                      Sur le site, un bandeau de texte : le titre, le texte et le bouton. Tout visiteur le voit pendant la période.
                    </Typography>
                  )}
                </Box>
                <TextField id="offre-titre" label="Titre" value={edition.titre} onChange={champ('titre')} inputProps={{ maxLength: 120 }} fullWidth />
                <TextField id="offre-texte" label="Texte" value={edition.texte} onChange={champ('texte')} inputProps={{ maxLength: 600 }} multiline minRows={3} fullWidth
                  helperText="Une ou deux phrases, prix compris. Ni pronom pour l’enfant, ni compte à rebours." />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField id="offre-bouton" label="Bouton" value={edition.bouton} onChange={champ('bouton')} inputProps={{ maxLength: 60 }} fullWidth />
                  <TextField id="offre-lien" label="Lien" value={edition.lien} onChange={champ('lien')} fullWidth
                    helperText="Chemin du portail parent, ou https://…" />
                </Stack>
                {surLeSite(edition) && (
                  <TextField id="offre-lien-site" label="Lien du bandeau sur le site" value={edition.lienSite} onChange={champ('lienSite')} fullWidth
                    helperText="Chemin du site (« /boutique.html#bientot ») ou https://… Vide : le lien ci-dessus, sur le portail parent." />
                )}
                <TextField id="offre-produit" select label="Produit" value={edition.produitCode} onChange={champ('produitCode')} fullWidth
                  helperText="L’offre ne part que si ce produit est en vente.">
                  <MenuItem value="">Aucun (abonnement)</MenuItem>
                  {(donnees?.produits || []).map((p) => (
                    <MenuItem key={p.code} value={p.code}>{p.libelle} ({p.code}){p.actif ? '' : ' : fermé'}</MenuItem>
                  ))}
                </TextField>
                {edition.surfaces.includes('mail') && <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Publics des mails (l’offre de l’enfant)</Typography>
                  <FormGroup row>
                    {PUBLICS.map((p) => (
                      <FormControlLabel key={p.cle} label={p.libelle}
                        control={<Checkbox id={`offre-public-${p.cle}`} checked={edition.publics.includes(p.cle)} onChange={() => basculerPublic(p.cle)} />} />
                    ))}
                  </FormGroup>
                </Box>}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <TextField id="offre-priorite" label="Priorité" type="number" value={edition.priorite} onChange={champ('priorite')}
                    helperText="La plus haute l’emporte si deux offres se chevauchent." sx={{ maxWidth: 220 }} />
                  <FormControlLabel label="Active"
                    control={<Switch id="offre-active" checked={edition.actif} onChange={(e) => setEdition((x) => ({ ...x, actif: e.target.checked }))} />} />
                </Stack>
                <TextField id="offre-note" label="Note interne" value={edition.note} onChange={champ('note')} multiline fullWidth />
              </Stack>
            </DialogContent>
          )}
          <DialogActions>
            {edition?.id && <Button color="error" onClick={supprimer} disabled={enCours} sx={{ mr: 'auto' }}>Supprimer l’offre partout</Button>}
            <Button onClick={() => setEdition(null)} disabled={enCours}>Annuler</Button>
            <Button variant="contained" onClick={enregistrer} disabled={enCours}>Enregistrer</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
}
